import type { RealtimeMessage } from '@going-once/contracts/channels';

import type { OutputOf } from './generated/contracts';

/**
 * The rules a live-show screen needs, on any platform — the `auctions.ts` of shows.
 *
 * **These were in `apps/web/src/screens/` and a second client is exactly why they moved.**
 * The web viewer and the web studio each carried their own copy of the chat reducer, and
 * the phone was about to carry two more. None of it is about rendering: which message
 * replaces which, whether a heart is our own echo, whether a listing can go on the block.
 */

export type ChatMessage = OutputOf<'shows.chat.history'>['messages'][number];
export type LineupEntry = OutputOf<'shows.show.get'>['lineup'][number];
export type SellerListing = OutputOf<'catalog.listing.list'>['listings'][number];
export type Lot = OutputOf<'auctions.auction.current'>['live'][number];

/**
 * How many messages a chat rail holds.
 *
 * A live room runs for an hour and nobody scrolls back through it over video — the rail is
 * the last few things said. Holding the whole show grows a list nobody reads until the
 * screen is slow, and `shows.chat.history` is one call away for anything further back.
 */
export const CHAT_WINDOW = 40;

/** How often a viewer says "still here". Half the server's bucket, so nobody flickers out of the count. */
export const HEARTBEAT_MS = 30_000;

/**
 * A room message applied to the rail — **applied directly rather than by refetching**,
 * unlike a bid. A chat message is complete in the message, whereas a bid is a nudge to
 * re-read state the payload is deliberately smaller than. That split is why there are two
 * channels at all.
 *
 * Returns the same array when nothing changed, so a caller's `setState` is a no-op.
 */
export function applyRoomMessage(chat: ChatMessage[], message: RealtimeMessage): ChatMessage[] {
  const data = (message.data ?? {}) as Record<string, unknown>;
  const messageId = typeof data.messageId === 'string' ? data.messageId : undefined;
  if (!messageId) return chat;

  if (message.kind === 'chat') {
    // A room can move faster than a render: dropping a duplicate is cheaper than a key
    // collision, and the sender sees their own message arrive this way too.
    if (chat.some((entry) => entry.messageId === messageId)) return chat;
    return [
      ...chat.slice(-CHAT_WINDOW),
      {
        messageId,
        at: message.at,
        displayName: String(data.displayName ?? 'Someone'),
        body: String(data.body ?? ''),
        removed: false,
      },
    ];
  }
  if (message.kind === 'chat-removed') return chat.filter((entry) => entry.messageId !== messageId);
  return chat;
}

/** A message this client posted itself, merged without waiting for its echo. */
export function appendOwnMessage(chat: ChatMessage[], message: ChatMessage): ChatMessage[] {
  return chat.some((entry) => entry.messageId === message.messageId) ? chat : [...chat.slice(-CHAT_WINDOW), message];
}

/**
 * How many hearts a room message adds — zero for our own echo.
 *
 * A tap animates immediately, because a heart that waits for a round trip feels broken, and
 * the same reaction then arrives back on the channel like everyone else's. `ownNonces` is
 * the set this client sent; a match is consumed and counts nothing. Without it every one of
 * your own hearts appeared twice.
 */
export function reactionCount(message: RealtimeMessage, ownNonces: Set<string>): number {
  if (message.kind !== 'reaction') return 0;
  const data = (message.data ?? {}) as Record<string, unknown>;
  const nonce = typeof data.nonce === 'string' ? data.nonce : undefined;
  if (nonce && ownNonces.delete(nonce)) return 0;
  return Number(data.count ?? 1);
}

/**
 * Why this listing cannot go on the block right now, in the seller's words — or
 * `undefined` if it can.
 *
 * **Advice, not enforcement.** The engine re-checks every one of these at launch through
 * the inventory lease, which is the only place it counts; this exists so a seller learns
 * before pressing rather than after. If the two ever disagree, a grayed row is wrong — not
 * a sale.
 *
 * It reads the seller's own listings rather than widening `shows.show.get`: a public read
 * that fanned out into Catalog for every lineup entry would put a cross-domain join on a
 * guest path to answer a question only the seller asks.
 */
export function blockedBecause(listing: SellerListing | undefined): string | undefined {
  // No listing in the seller's page of results: say nothing and let the server decide.
  if (!listing) return undefined;
  switch (listing.status) {
    case 'sold':
      return 'Sold — this one is gone';
    case 'draft':
      return 'Not published yet';
    case 'archived':
      return 'Archived';
    case 'taken_down':
      return 'Taken down';
    case 'at_auction':
      // Running, but not in this show — otherwise the lot's own state would have shown it.
      return 'Running in another show';
    default:
      break;
  }
  if (!listing.saleModes.includes('auction')) return 'Not set up for auction';
  if (listing.startPriceCents === undefined) return 'No starting price';
  if (listing.quantityAvailable <= 0) return 'Out of stock';
  return undefined;
}

/**
 * Each planned lot against what actually happened to it.
 *
 * **The lineup is a plan and the auction is the fact** (D47), so the join is done by the
 * client rather than stored on the lineup. Live wins over finished: a relaunched lot has
 * two auctions and the running one is the one the seller is holding a hammer over.
 */
export function lotsByListing(live: Lot[], done: Lot[]): Map<string, { lot: Lot; running: boolean }> {
  const byListing = new Map<string, { lot: Lot; running: boolean }>();
  for (const lot of done) byListing.set(lot.listingRef, { lot, running: false });
  for (const lot of live) byListing.set(lot.listingRef, { lot, running: true });
  return byListing;
}

/**
 * What the channel is doing, in the words that point at the thing to fix — for the one
 * screen that is a control panel. "Not configured" is `config.json`; "failed" is the
 * handshake; conflating them sends somebody to read WebSocket frames over a missing deploy.
 */
export function describeChannel(state: { status: string; live: boolean }): string {
  if (state.live) return 'connected';
  if (state.status === 'unconfigured') return 'not configured — check config.json';
  if (state.status === 'error') return 'failed, polling';
  return 'connecting';
}

/**
 * A stable index per name, into whatever palette the caller renders with.
 *
 * Derived rather than stored: the same person is the same color on every device with no
 * lookup, and nothing to migrate when a name changes.
 */
export function nameHash(name: string, buckets: number): number {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 997;
  return hash % buckets;
}

/** The seller onboarding steps, in the seller's words. The server decides the order (D9). */
export const SELLER_STEP_LABEL: Record<string, string> = {
  phone_verification: 'Verify a phone number',
  stripe_kyc: 'Set up payouts',
  storefront_profile: 'Name your storefront',
};
